import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';

export interface MapSkin {
  id: string;
  label: string;
  description: string;
  mapId: string;
  thumbnail: string;
  isDepth?: boolean;
}

export const MAP_SKIN_CATALOG: {
  classic: MapSkin[];
  all: MapSkin[];
  defaultSkin: MapSkin;
  byId: (id: string | null | undefined) => MapSkin;
} = {
  classic: [
    {
      id: 'nautical',
      label: 'Nautical',
      description: 'Soft tones. The Esperwave standard.',
      mapId: '019d17a5-5f92-7029-87a0-693d16b5fd64',
      thumbnail: '/assets/images/map_skins/nautical.png',
    },
    {
      id: 'vivid',
      label: 'Vivid',
      description: 'Bright water, high-contrast depth.',
      mapId: '01a01ef4-1bf8-7702-a1f1-2f3abce22809',
      thumbnail: '/assets/images/map_skins/vivid.png',
    },
    {
      id: 'night',
      label: 'Night',
      description: 'Dark chart for low-light running.',
      mapId: '01a01f20-5e2d-7575-a76c-0dac27f15785',
      thumbnail: '/assets/images/map_skins/night.png',
    },
    {
      id: 'satellite',
      label: 'Satellite',
      description: 'Aerial imagery under the contours.',
      mapId: '01a0388c-5b6e-7fdf-aa9f-6cba7a217f06',
      thumbnail: '/assets/images/map_skins/satellite.jpg',
    },
  ],
  get all() {
    return this.classic;
  },
  get defaultSkin() {
    return this.classic[0];
  },
  byId(id: string | null | undefined): MapSkin {
    return this.all.find((s) => s.id === id) || this.defaultSkin;
  },
};

@Injectable({
  providedIn: 'root',
})
export class MapSkinService {
  private readonly storageKey = 'esperwave_selected_map_skin';
  private selectedSkinIdSubject = new BehaviorSubject<string>(this.getStoredSkinId());
  selectedSkinId$ = this.selectedSkinIdSubject.asObservable();

  private getStoredSkinId(): string {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored && MAP_SKIN_CATALOG.all.some((s) => s.id === stored)) {
        return stored;
      }
    } catch {
      // Fallback if localStorage unavailable
    }
    return MAP_SKIN_CATALOG.defaultSkin.id;
  }

  get currentSkinId(): string {
    return this.selectedSkinIdSubject.value;
  }

  get currentSkin(): MapSkin {
    return MAP_SKIN_CATALOG.byId(this.currentSkinId);
  }

  getStyleUrlFor(skinId: string): string {
    const skin = MAP_SKIN_CATALOG.byId(skinId);
    const key = environment.maptilerKey || 'IfvuVnjKOAzdw0Wrs5eg';
    return `https://api.maptiler.com/maps/${skin.mapId}/style.json?key=${key}`;
  }

  getCurrentStyleUrl(): string {
    return this.getStyleUrlFor(this.currentSkinId);
  }

  selectSkin(skinId: string): void {
    if (this.selectedSkinIdSubject.value === skinId) return;
    try {
      localStorage.setItem(this.storageKey, skinId);
    } catch {}
    this.selectedSkinIdSubject.next(skinId);
  }

  resetSkin(): void {
    this.selectSkin(MAP_SKIN_CATALOG.defaultSkin.id);
  }
}
